/**
 * Room folder reconciliation — keeps ROOM groups ("room tags") in step with
 * the room annotations they were derived from.
 *
 * Single owner of folder AUTO-CREATION: every room creation path (magic room,
 * manual draw, old projects on first load) converges here, because the hook
 * reacts to the annotation TREE context — the same source the left panel
 * renders from — rather than a standalone cache read.
 *
 * Association is positional (anchor containment; see RoomLabelsLayer), so no
 * name or room syncing happens here. Per reconcile pass:
 *  - a ROOM annotation on a floor with an unset `room_group_link` gets a
 *    folder created under that floor (link + centroid anchor written on
 *    success); the ROOM_GROUP_LINK_NONE tombstone (user deleted the tag) is
 *    honored;
 *  - an existing unlinked folder with the room's deterministic id is adopted
 *    by just rewriting the link (lost-write recovery, cross-client races);
 *  - a linked folder without a stored anchor gets one backfilled at the room
 *    polygon's centroid (folders created before anchors existed);
 *  - a linked folder on the wrong floor is dissolved and recreated on the
 *    next pass under the right floor;
 *  - an auto folder whose room annotation is gone is dissolved (children
 *    re-parent to the floor server-side); manual folders are never touched.
 */

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getAnnotationNameProperty,
  getRoomGroupLinkProperty,
  getRoomGroupPositionProperty,
  getRoomPolygonProperty,
  hasGlobalPropertyService,
  ROOM_GROUP_LINK_NONE,
} from '@pointorama/potree-shared';
import { GroupType } from '../../types/graphqlTypes';
import { AnnotationContextGroup, isAnnotationContextGroup } from '../../contexts/AnnotationContext';
import { useAnnotationTree } from './useAnnotationContext';
import { useGroupOperations } from './useGroupOperations';
import { isAnnotationMutationPending, onAnnotationMutationSettled } from './annotationMutationCoalescer';
import { polygonCentroid, roomFolderIdForRoom, roomIdFromAutoFolderId } from '../../utils/roomFolders';

export const useRoomFolderSync = (): void => {
  const { projectId = '' } = useParams();
  const tree = useAnnotationTree();
  const { createGroup, ungroup } = useGroupOperations();

  /** Identifiers with a mutation in flight — prevents duplicate storms within a session. */
  const inFlightRef = useRef(new Set<string>());
  /**
   * Folder ids whose create failed (DUPLICATE_GROUP_IDENTIFIER, permissions):
   * never retried until the folder becomes visible in the tree. Without this
   * a folder that exists server-side but stays invisible client-side would be
   * re-attempted on every pass — an endless duplicate-error storm.
   */
  const failedCreatesRef = useRef(new Set<string>());
  /** Bumped when a deferred annotation mutation settles, to retry skipped rooms. */
  const [settleTick, setSettleTick] = useState(0);

  useEffect(() => {
    if (!projectId || tree.length === 0 || !hasGlobalPropertyService()) return;
    const inFlight = inFlightRef.current;
    // Rooms whose own create/delete hasn't been confirmed yet are skipped
    // this pass: firing a folder mutation now would return a full project
    // state WITHOUT the optimistic room, clobbering it from the cache (the
    // room then flickers out, the dissolve pass tears the folder down, and
    // the room's own response brings everything back — an oscillation).
    let sawPendingRoom = false;

    // ─── Collect rooms + room folders from the tree ─────────────────────
    const rooms: { roomId: string; floorId: string }[] = [];
    const foldersById = new Map<string, AnnotationContextGroup>();
    const roomIdsInProject = new Set<string>();
    /** Floors that currently have a wall sketch — gates the tombstone rule below. */
    const floorsWithSketch = new Set<string>();
    for (const item of tree) {
      if (!isAnnotationContextGroup(item)) continue;
      if (item.type === GroupType.Room) {
        foldersById.set(item.identifier, item); // orphan-fallback folder at root
        continue;
      }
      const isFloor = item.type === GroupType.Floor;
      for (const child of item.annotations) {
        if (isAnnotationContextGroup(child)) {
          if (child.type === GroupType.Room) foldersById.set(child.identifier, child);
        } else if (child.type === 'ROOM') {
          roomIdsInProject.add(child.identifier);
          if (isFloor) rooms.push({ roomId: child.identifier, floorId: item.identifier });
        } else if (child.type === 'WALL_SKETCH' && isFloor) {
          floorsWithSketch.add(item.identifier);
        }
      }
    }

    // A previously failed create clears once the folder is actually visible.
    const failedCreates = failedCreatesRef.current;
    for (const folderId of foldersById.keys()) failedCreates.delete(folderId);

    const readLink = (roomId: string): string | null => {
      try {
        return getRoomGroupLinkProperty(roomId).value ?? '';
      } catch {
        return null; // property service hiccup — skip this pass
      }
    };

    // ─── Ensure a folder per room ───────────────────────────────────────
    for (const { roomId, floorId } of rooms) {
      if (isAnnotationMutationPending(roomId)) {
        sawPendingRoom = true;
        continue;
      }
      let link = readLink(roomId);
      if (link === null) continue;
      if (link === ROOM_GROUP_LINK_NONE) {
        // Tombstones only suppress folder auto-creation while the floor has
        // NO wall sketch (the post-cascade state — deleting the sketch
        // removed the folders and must not have them instantly resurrect
        // from the surviving room measures). Once a wall sketch exists,
        // folders are managed content again: clear the tombstone and let
        // the normal adopt-or-create flow below regenerate the folder. This
        // also self-heals floors whose tombstones were left behind by
        // earlier flows.
        if (!floorsWithSketch.has(floorId)) continue;
        try {
          getRoomGroupLinkProperty(roomId).value = '';
          link = '';
        } catch {
          continue; // property service hiccup — retried on the next pass
        }
      }

      const linkedFolder = link ? foldersById.get(link) : undefined;
      if (linkedFolder) {
        // Wrong-floor drift → dissolve, recreated on the next pass under the
        // right floor. Names/rooms are NOT synced (positional model).
        if (linkedFolder.parentIdentifier !== floorId) {
          if (!inFlight.has(link)) {
            inFlight.add(link);
            void ungroup(link)
              .then(() => {
                getRoomGroupLinkProperty(roomId).value = '';
              })
              .catch(() => undefined)
              .finally(() => inFlight.delete(link));
          }
          continue;
        }
        // Backfill a missing anchor (folders created/adopted before anchors
        // were written): seed at the room polygon's centroid so the tag
        // renders and participates in positional filing.
        try {
          const anchor = getRoomGroupPositionProperty(link).value;
          if (!anchor || (anchor.x === 0 && anchor.y === 0 && anchor.z === 0)) {
            const centroid = polygonCentroid(getRoomPolygonProperty(roomId).value);
            if (centroid) getRoomGroupPositionProperty(link).value = centroid;
          }
        } catch {
          // property service hiccup — retried on the next pass
        }
        continue;
      }

      // No usable link. Adopt an existing folder before creating: the
      // deterministic id may already exist when a previous link write was
      // lost (hot reload, crash) or another client created it.
      const folderId = link || roomFolderIdForRoom(roomId);
      if (foldersById.has(folderId)) {
        try {
          getRoomGroupLinkProperty(roomId).value = folderId;
        } catch {
          // property service hiccup — retried on the next pass
        }
        continue;
      }

      if (inFlight.has(folderId) || failedCreates.has(folderId)) continue;
      inFlight.add(folderId);
      // Seed the tag's name from the measure when the user renamed it; the
      // default "Room as-built" (or the legacy "Room measure" in older
      // projects) would make an ugly tag name, so fall back to plain "Room".
      let name = 'Room';
      try {
        const measureName = getAnnotationNameProperty(roomId).value;
        if (measureName && measureName !== 'Room as-built' && measureName !== 'Room measure') name = measureName;
      } catch {
        // fall back to the default name
      }
      void createGroup({ identifier: folderId, name, type: GroupType.Room, annotationIdentifiers: [], parentIdentifier: floorId })
        .then(() => {
          getRoomGroupLinkProperty(roomId).value = folderId;
          try {
            const centroid = polygonCentroid(getRoomPolygonProperty(roomId).value);
            if (centroid) getRoomGroupPositionProperty(folderId).value = centroid;
          } catch {
            // polygon not readable yet — the anchor backfill above recovers
          }
        })
        .catch(() => {
          // Duplicate id (another client won the race — adopt it by writing
          // the link so no further create is attempted) or missing
          // permission; never a tombstone. Also park the id so it isn't
          // re-attempted while the folder stays invisible client-side.
          failedCreates.add(folderId);
          try {
            getRoomGroupLinkProperty(roomId).value = folderId;
          } catch {
            // property service hiccup — retried on the next pass
          }
        })
        .finally(() => inFlight.delete(folderId));
    }

    // ─── Dissolve auto folders whose room annotation is gone ────────────
    for (const folder of foldersById.values()) {
      const sourceRoomId = roomIdFromAutoFolderId(folder.identifier);
      if (!sourceRoomId) continue; // manual folder — user-managed
      if (roomIdsInProject.has(sourceRoomId)) continue;
      if (isAnnotationMutationPending(sourceRoomId)) {
        // The room may only be missing because a racing full-state response
        // clobbered its optimistic entry — don't tear the folder down yet.
        sawPendingRoom = true;
        continue;
      }
      if (inFlight.has(folder.identifier)) continue;
      inFlight.add(folder.identifier);
      void ungroup(folder.identifier)
        .catch(() => undefined)
        .finally(() => inFlight.delete(folder.identifier));
    }

    // Skipped rooms need a revisit even if the confirming response leaves the
    // tree structurally identical (the optimistic entry already matched) —
    // re-run one pass when the next deferred mutation settles.
    if (sawPendingRoom) {
      let done = false;
      const off = onAnnotationMutationSettled(() => {
        if (done) return;
        done = true;
        off();
        setSettleTick((t) => t + 1);
      });
      return off;
    }
  }, [tree, projectId, createGroup, ungroup, settleTick]);
};
